import { Capacitor } from "@capacitor/core";
import { Dialog } from "@capacitor/dialog";
import { interfaceTranslator } from "@/i18n/translate";

/**
 * Test seam — production code calls `window.location.assign(...)`,
 * which jsdom locks down and isn't safely overridable. Tests override
 * this via `__setSettingsNavigatorForTesting(...)` to observe the URL
 * without touching the real `Location` object.
 */
let _settingsNavigator = (url: string) => {
  window.location.assign(url);
};

/** @internal — test-only escape hatch. Do not call from production code. */
export function __setSettingsNavigatorForTesting(
  fn: (url: string) => void
): () => void {
  const previous = _settingsNavigator;
  _settingsNavigator = fn;
  return () => {
    _settingsNavigator = previous;
  };
}

/**
 * Attempt to open the OS-level app settings page so the user can grant
 * permissions that have been permanently denied.
 *
 * - On iOS, navigates the WebView to `app-settings:`. WKWebView
 *   intercepts the scheme and hands it to `UIApplication`, which
 *   opens the app's own settings page (where every per-app toggle
 *   — notifications, microphone, etc. — is reachable).
 *   `@capacitor/app` v8 removed the dedicated `openUrl` method, so
 *   we rely on this WebView fallback that still works in production.
 * - On Android, opening the per-app settings screen requires an
 *   intent (`ACTION_APPLICATION_DETAILS_SETTINGS`) and there is no
 *   plugin in this project that exposes it today. We return `false`
 *   and rely on the caller to surface a manual fallback (e.g. a
 *   toast pointing the user to Android Settings > Apps > Vidimo).
 * - On the web build, this is a no-op (no OS settings to open) and
 *   returns `false`.
 *
 * @returns `true` if a native settings page was attempted; `false`
 * otherwise.
 */
export function openAppSettings(): boolean {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  const platform = Capacitor.getPlatform();
  if (platform === "ios") {
    _settingsNavigator("app-settings:");
    return true;
  }

  // Android: no supported path through @capacitor/app today. Caller
  // should show fallback guidance.
  return false;
}

/**
 * WhatsApp-style permission-recovery flow for a permanently-denied
 * permission. Shows a **native OS alert** (UIAlertController on iOS /
 * AlertDialog on Android) with a "Cancel" / "Settings" choice. Tapping
 * "Settings" deep-links to the app's own OS settings page via
 * {@link openAppSettings}; if no deep link is available (Android / web)
 * we surface a second native alert with manual guidance.
 *
 * Always resolves; never throws.
 *
 * @param opts.title   Short alert title (e.g. "Microphone access needed").
 * @param opts.message Explanation shown in the alert body.
 * @param opts.settingsGuidance Manual-steps fallback shown when no
 *   deep link is available.
 * @returns `true` if a native recovery dialog was shown, `false` on the
 *   web build (no OS settings page to reach). Callers should use the
 *   `false` case to fall back to their own feedback (e.g. an in-app
 *   error) so a denial isn't swallowed silently.
 */
export async function promptToOpenAppSettings(opts: {
  title: string;
  message: string;
  settingsGuidance: string;
}): Promise<boolean> {
  // No native settings UI on the web build — signal that nothing was
  // shown so the caller can surface its own feedback instead.
  if (!Capacitor.isNativePlatform()) {
    return false;
  }

  const t = interfaceTranslator("shell.osHelpers");
  const { value } = await Dialog.confirm({
    cancelButtonTitle: t("cancel"),
    message: opts.message,
    okButtonTitle: t("settings"),
    title: opts.title,
  });
  // The user saw the alert; "Cancel" is a deliberate dismissal, so we
  // still consider native recovery UI to have been shown.
  if (!value) {
    return true;
  }

  const opened = await openAppSettings();
  if (!opened) {
    await Dialog.alert({
      message: opts.settingsGuidance,
      title: opts.title,
    });
  }
  return true;
}
